import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, effect, inject, input, signal } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppFeatureService, FEATURE_KEY_TO_COLLATERAL_TYPE_KEYS } from '@nk/access-block';
import { COLLATERAL_API_SERVICE_TOKEN, CollateralApiService } from '@nk/api-lib';
import {
  AppFeatureKey,
  CompanyCollateralAggregateEntity,
  CompanyCollateralCreditorEntity,
  CompanyCollateralTypeEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { ButtonDirective, CardContentComponent, ProgressComponent } from '@nk/ui-lib';
import {
  DictList,
  arrayMax,
  arrayToDict,
  arrayToDictList,
  arrayUnique,
  includes,
  titleCaseCompanyName,
} from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import * as Highcharts from 'highcharts';
import { SeriesAreaOptions, SeriesColumnOptions } from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { BehaviorSubject, Observable, combineLatest, of, switchMap } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { getColorClass } from '../../utils/collateral.utils';

interface Options {
  showForCorporateGroup: boolean;
}

@Component({
  selector: 'nk-company-collateral-chart',
  templateUrl: './company-collateral-chart.component.html',
  imports: [
    AsyncPipe,
    TranslateModule,
    HighchartsChartModule,
    ProgressComponent,
    ButtonDirective,
    CardContentComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyCollateralChartComponent implements OnInit {
  public companyUuid = input.required<string>();
  public showForCorporateGroup = input(false);

  public highcharts: typeof Highcharts = Highcharts; // required
  public chartOptions$!: Observable<Highcharts.Options>;

  public loading = signal(false);
  public loadingChart = signal(false);

  public relevantCollateralTypes$!: Observable<WithUuid<CompanyCollateralTypeEntity>[]>;

  private _optionsChange = new BehaviorSubject<Options | null>(null);

  public selectedCollateralType = signal<string | null>(null);
  private _selectedCollateralType = new BehaviorSubject<string | null>(null);
  public selectedCollateralType$ = this._selectedCollateralType.asObservable();

  private _collateralAggregate$!: Observable<CompanyCollateralAggregateEntity[]>;
  public creditorSliceWarning = signal(false);
  public maxCreditors = 20;
  public hasDataRows$!: Observable<boolean>;

  private _collateralService = inject<CollateralApiService>(COLLATERAL_API_SERVICE_TOKEN);
  private _appFeatureService = inject(AppFeatureService);
  private _translateService = inject(TranslateService);

  constructor() {
    effect(() => {
      this._optionsChange.next({
        showForCorporateGroup: this.showForCorporateGroup(),
      });
    });
  }

  public ngOnInit(): void {
    this._collateralAggregate$ = this._optionsChange.pipe(
      filter((options) => options !== null),
      tap(() => this.loadingChart.set(true)),
      switchMap((options) => {
        const useQuarterlyDataPoints = false;
        return this._collateralService.companyCollateralAggregateByCompanyCache.get(
          this.companyUuid(),
          options.showForCorporateGroup,
          useQuarterlyDataPoints,
          null,
          null,
        );
      }),
      tap(() => this.loadingChart.set(false)),
    );

    this.relevantCollateralTypes$ = combineLatest([
      this._collateralAggregate$,
      this._collateralService.companyCollateralTypeListCache.get(),
      this._appFeatureService.activeAppFeatureDimsCache.get(),
      this._optionsChange,
    ]).pipe(
      map(([aggregate, collateralTypes, activeAppFeatureDims]) => {
        // possible values from the aggregate.
        const collateralTypeOptions = arrayUnique(aggregate.map((a) => a.company_collateral_type_uuid));

        // get the full type with name.
        let collateralTypesFiltered = collateralTypes.filter((a) => collateralTypeOptions.includes(a.uuid));

        // leasingavtale has no amount, and makes no sense in this chart
        collateralTypesFiltered = collateralTypesFiltered.filter(
          (a) => a.collateral_type_key !== 'norwegian_movable_property_register^^^leasingavtale',
        );

        if (!activeAppFeatureDims.flags.norwegian_property_data) {
          // Do feature checking.
          const excludedTypes = FEATURE_KEY_TO_COLLATERAL_TYPE_KEYS[AppFeatureKey.norwegian_property_data];
          collateralTypesFiltered = collateralTypesFiltered.filter((a) => {
            return !includes(excludedTypes, a.collateral_type_key);
          });
        }
        return collateralTypesFiltered;
      }),
      tap((collateralTypes) => {
        // We might want to execute this again when we get corporate data.
        const collateralTypeUuids = collateralTypes.map((a) => a.uuid);
        if (collateralTypeUuids.length) {
          const currentSelected = this.selectedCollateralType();
          if (currentSelected === null) {
            this.setCollateralType(collateralTypeUuids[0]);
          } else if (!collateralTypeUuids.includes(currentSelected)) {
            // the collateral type selected is not one of the available ones. (can happen if one changes from corporate group and back)
            this.setCollateralType(collateralTypeUuids[0]);
          }
        }
      }),
    );

    this.hasDataRows$ = this._collateralAggregate$.pipe(map((aggregate) => aggregate.length > 0));

    this.chartOptions$ = this.selectedCollateralType$.pipe(
      tap(() => this.loading.set(true)),
      filter((selectedCollateralType) => !!selectedCollateralType),
      switchMap((selectedCollateralType) =>
        this._collateralAggregate$.pipe(
          switchMap((collateralAggregate) => {
            const creditorUuids = arrayUnique(
              collateralAggregate
                .filter((a) => a.company_collateral_type_uuid === selectedCollateralType)
                .map((a) => a.company_collateral_creditor_uuid),
            );
            const creditors$ = creditorUuids.length
              ? this._collateralService.companyCollateralCreditorListCache.getStableCache(creditorUuids)
              : of<WithUuid<CompanyCollateralCreditorEntity>[]>([]);
            return creditors$.pipe(
              map((creditors) => [selectedCollateralType, collateralAggregate, creditors] as const),
            );
          }),
        ),
      ),
      map(([selectedCollateralType, collateralAggregate, creditors]) => {
        const collateralAggregateByCreditor = collateralAggregate
          .filter((a) => a.company_collateral_type_uuid === selectedCollateralType)
          .sort((a, b) => {
            if (a.snapshot_date.valueOf() == b.snapshot_date.valueOf()) {
              return (a.amount ?? 0) - (b.amount ?? 0);
            } else {
              return a.snapshot_date.valueOf() - b.snapshot_date.valueOf();
            }
          });
        let collateralAggregateByCreditorDictList = arrayToDictList(
          this.mergeSumAndCountForAggregate(collateralAggregateByCreditor),
          (agg) => agg.company_collateral_creditor_uuid,
        );
        collateralAggregateByCreditorDictList = this._appendZeroValuesToBeginning(
          collateralAggregateByCreditorDictList,
        );
        let seriesBars = this.sortSeriesByDateValue(
          this.getChartSeriesFromArrayDictBars(collateralAggregateByCreditorDictList, creditors),
        );
        if (seriesBars.length > this.maxCreditors) {
          seriesBars = seriesBars.slice(0, this.maxCreditors);
          this.creditorSliceWarning.set(true);
        }
        return this.chartOptions(seriesBars);
      }),
      tap(() => this.loading.set(false)),
    );
  }

  public setCollateralType(selectedCollateralTypeUuid: string): void {
    this.selectedCollateralType.set(selectedCollateralTypeUuid);
    this._selectedCollateralType.next(selectedCollateralTypeUuid);
  }

  /*
   * MergeSumAndCount
   * If showForCorporateGroup we will get data points for multiple companies, then we need to sum the amount and count so we get one point for all companies.
   * */
  public mergeSumAndCountForAggregate(
    collateralAggregateByCreditor: CompanyCollateralAggregateEntity[],
  ): CompanyCollateralAggregateEntity[] {
    return collateralAggregateByCreditor.reduce((final: CompanyCollateralAggregateEntity[], currValue) => {
      const existsIndex = final.findIndex(
        (f) =>
          f.snapshot_date.getTime() === currValue.snapshot_date.getTime() &&
          f.company_collateral_creditor_uuid == currValue.company_collateral_creditor_uuid,
      );
      if (existsIndex >= 0) {
        const item = final[existsIndex];
        final[existsIndex] = {
          ...item,
          amount: (currValue.amount ?? 0) + (item.amount ?? 0),
          collateral_count: (currValue.collateral_count ?? 0) + (item.collateral_count ?? 0),
        };
        return final;
      } else {
        return [...final, currValue];
      }
    }, []);
  }

  public sortSeriesByDateValue(seriesBars: SeriesAreaOptions[]): SeriesAreaOptions[] {
    return seriesBars.sort((creditorA, creditorB) => {
      // data is constructed locally as [timestamp, amount] tuples in getChartSeriesFromArrayDictBars.
      const dataA = (creditorA.data ?? []) as [number, number][];
      const dataB = (creditorB.data ?? []) as [number, number][];
      const dateA = arrayMax(dataA, (value) => value[0]);
      const dateB = arrayMax(dataB, (value) => value[0]);
      return (dateB?.[1] ?? 0) - (dateA?.[1] ?? 0);
    });
  }

  /**
   * getChartSeriesFromArrayDictBars
   * @param collateralAggregateByCreditor: List of aggregated sum/count per time, per creditor
   * @param creditors: A list of all creditors for name lookup
   * We want to show one series per creditor.
   * We need to return an array of date.utc and amount.
   * It needs to be an array to improve perforamce for companies with a lot of collateral, see Obos Block Watne for instance.
   * If we don't use date utc with hour 0 minutes 0 we will run into issues with daylight saving time unless we configure highcharts.
   */
  public getChartSeriesFromArrayDictBars(
    collateralAggregateByCreditor: DictList<CompanyCollateralAggregateEntity>,
    creditors: WithUuid<CompanyCollateralCreditorEntity>[],
  ): SeriesAreaOptions[] {
    const creditorDict = arrayToDict(creditors, (a) => a.uuid);
    //https://www.highcharts.com/demo/bar-stacked
    return Object.keys(collateralAggregateByCreditor).map((creditorUuid) => {
      const creditor = creditorDict[creditorUuid];
      const colorClass =
        creditor && getColorClass(creditor.creditor_name)
          ? `highcharts-color-${getColorClass(creditor.creditor_name)}`
          : undefined; // Setting class as undefined makes highchart use their default colors.
      return {
        data: collateralAggregateByCreditor[creditorUuid].map((a): [number, number] => {
          // Return data in an array to improve performance, important for companies with a lot of collateral.
          return [
            Date.UTC(a.snapshot_date.getFullYear(), a.snapshot_date.getMonth(), a.snapshot_date.getDate(), 0, 0),
            a.amount ?? 0,
          ];
        }),
        name: (creditor ? titleCaseCompanyName(creditor.creditor_name) : null) ?? 'unknown',
        type: 'area',
        className: colorClass,
      } satisfies SeriesAreaOptions; // Prevents literal-type widening on type: 'area'
    });
  }

  /**
   * chartOptionsStackedBars
   * @param series: Highchart series with creditor and data with amount.
   * If a company has too much data it will not render unless turboThreshold is set. See obos block watne.
   * We want to show quarterly months, so we set units to be months 3 and year.
   * We have placed the y-axis labels on the opposite (right) side, so the gray backgorund area lines up with the table below. MarginLeft 0 also makes it line up.
   */
  public chartOptionsStackedBars(series: SeriesColumnOptions[]): Highcharts.Options {
    return {
      chart: {
        type: 'column',
        styledMode: true,
        marginLeft: 0,
        panKey: 'shift',
        zoomType: 'x',
        panning: true,
      },
      title: { text: '' },
      yAxis: { title: { text: '' }, opposite: true, min: 0 },
      xAxis: {
        type: 'datetime',
        units: [
          ['month', [3]],
          ['year', null],
        ],
      },
      legend: { enabled: true },
      tooltip: {
        shared: true,
        headerFormat: '<span style="font-size:12px"><b>{point.key}</b></span><br>',
        outside: true,
      },
      plotOptions: {
        column: { stacking: 'normal' },
        series: { turboThreshold: 100000 },
      },
      credits: { enabled: false },
      series: series,
    } as unknown as Highcharts.Options;
  }

  public chartOptions(series: SeriesAreaOptions[]): Highcharts.Options {
    return {
      chart: {
        type: 'area',
        styledMode: true,
        panKey: 'shift',
        zoomType: 'x',
        panning: true,
      },
      title: { text: '' },
      yAxis: { title: { text: '' }, opposite: true, min: 0 },
      xAxis: {
        title: {
          text: this._translateService.instant(extractTranslation('common.chart_zoom_help')),
        },
        type: 'datetime',
        units: [
          ['month', [3]],
          ['year', null],
        ],
      },
      legend: { enabled: true },
      tooltip: {
        shared: true,
        headerFormat: '<span style="font-size:12px"><b>{point.key}</b></span><br>',
      },
      plotOptions: {
        area: {
          allowPointSelect: false,
          trackByArea: true,
          stacking: 'normal',
          marker: { enabled: false },
        },
        series: { turboThreshold: 100000 },
      },
      credits: { enabled: false },
      series: series,
    } as unknown as Highcharts.Options;
  }

  private _appendZeroValuesToBeginning(
    collateralAggregateByCreditorDictList: DictList<CompanyCollateralAggregateEntity>,
  ): DictList<CompanyCollateralAggregateEntity> {
    const newDict = { ...collateralAggregateByCreditorDictList };
    Object.keys(newDict).map((creditorUuid) => {
      const listOfValues = newDict[creditorUuid];
      const earliestDataPoints = new Date('2019-02-02');
      // We started to get data around 2019, so appending 0 values to the beginning is wrong,
      // since it might have had more colateral before we started to track the data.
      const withinOurDataTime = listOfValues.find((a) => a.snapshot_date.valueOf() < earliestDataPoints.valueOf());

      if (withinOurDataTime) {
        return;
      }
      const date = listOfValues[0].snapshot_date;
      const newDate = dayjs(date).subtract(1, 'month').toDate();

      newDict[creditorUuid] = [
        {
          ...listOfValues[0],
          snapshot_date: newDate,
          amount: 0,
          collateral_count: 0,
        },
        ...listOfValues,
      ];
    });
    return newDict;
  }
}
